import React from "react";
import { Box, Radio, Typography } from "@mui/material";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { COLORS } from "../../assets/theme";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClientPackage } from "./types";
import { formatMoney } from "./classUtils";

interface RedeemablePackagePickerDialogProps {
  open: boolean;
  clientName?: string;
  dropInPrice?: number | string;
  packages: ClientPackage[];
  onClose: () => void;
  /** undefined means "charge the drop-in price instead of using a package". */
  onChoose: (clientPackageId?: string) => void;
  submitting?: boolean;
}

/**
 * Shown when there's a package decision to make instead of letting the backend
 * silently auto-pick. Single-session booking only — block enrollment prices
 * itself through /enroll/quote, which itemises the package split up front.
 */
const RedeemablePackagePickerDialog: React.FC<RedeemablePackagePickerDialogProps> = ({
  open,
  clientName,
  dropInPrice,
  packages,
  onClose,
  onChoose,
  submitting = false,
}) => {
  const [selected, setSelected] = React.useState<string>("");

  React.useEffect(() => {
    if (open) setSelected(packages[0]?.id ? String(packages[0].id) : "");
  }, [open, packages]);

  const rowSx = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1,
    border: "1px solid",
    borderColor: active ? COLORS.MainColor : "#eee",
    borderRadius: 2,
    p: 1.5,
    mb: 1,
    cursor: "pointer",
  });

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 520 }, maxWidth: 640 }}
      title="Which package should this book against?"
      actions={
        <>
          <CustomButton label="Cancel" variant="outlined" onClick={onClose} />
          <CustomButton
            label="Confirm"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            loading={submitting}
            disabled={submitting}
            onClick={() => onChoose(selected || undefined)}
          />
        </>
      }
    >
      <Typography sx={{ fontSize: 14, color: "#555", mb: 2 }}>
        {(() => {
          const who = clientName || "This client";
          return packages.length > 1
            ? `${who} has more than one package that covers this class — pick which one to redeem this session against, or charge drop-in instead.`
            : `${who} has a package that covers this class — use it for this session, or charge drop-in instead.`;
        })()}
      </Typography>

      <Box>
        {packages.map((pkg) => {
          const active = selected === String(pkg.id);
          return (
            <Box
              key={pkg.id}
              sx={rowSx(active)}
              onClick={() => setSelected(String(pkg.id))}
            >
              <Radio checked={active} size="small" sx={{ p: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {pkg.package?.name || "Package"}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "#777" }}>
                  {`${pkg.sessions_remaining ?? "-"} session${
                    pkg.sessions_remaining === 1 ? "" : "s"
                  } left · expires ${formatTableDate(pkg.expires_at)}`}
                </Typography>
              </Box>
            </Box>
          );
        })}

        <Box
          sx={rowSx(selected === "")}
          onClick={() => setSelected("")}
        >
          <Radio checked={selected === ""} size="small" sx={{ p: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              Don't use a package
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#777" }}>
              {`Charge the drop-in price instead — ${formatMoney(dropInPrice)}`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </CustomDialog>
  );
};

export default RedeemablePackagePickerDialog;
